import React, { useEffect, useState } from 'react';
import type { OpportunityProps, OpportunityStatusLegProps, RouterOrderProps, RouterPartnerProps, RouterContestProps } from '../../types';
import { Text, View } from '../../Components/Themed';
import { BetRouterApi } from '../api';
import { FlatList, TouchableOpacity } from 'react-native';
import { useColors } from '../../constants/useColors';
import { SearchBox, Icons } from '../../Components';
import Pagination from '../../Components/Pagination';
import PlacedOpportunityModal from './PlacedOpportunityModal';

type MyOpportunitiesProps = {
    partners:RouterPartnerProps[];
    compact?:boolean;
    insets?: { top: number; bottom: number; left: number; right: number };
    onBuyOpportunity?: (opportunity: any, legIndex?: number) => void;
}

const ITEMS_PER_PAGE = 5;

const MyOpportunities = ({ partners, compact = false, insets }:MyOpportunitiesProps) => {
    const Colors = useColors();

    const [ state, setState ] = useState<{
        loading:boolean,
        opportunities:OpportunityProps[],
        opportunity_status_legs: OpportunityStatusLegProps[],
        router_orders:RouterOrderProps[],
        router_contests:RouterContestProps[],
        searchText: string,
        currentPage: number,
        selectedOpportunity: OpportunityProps | null,
        showDetailModal: boolean
    }>({
        loading:false,
        opportunities:[],
        opportunity_status_legs:[],
        router_orders:[],
        router_contests:[],
        searchText: '',
        currentPage: 0,
        selectedOpportunity: null,
        showDetailModal: false
    });

    const { loading, opportunities, router_orders, router_contests, searchText, currentPage, selectedOpportunity, showDetailModal } = state;

    useEffect(() => {
        BetRouterApi.setEnvironment();
        getOpportunities();
    },[]);

    const getOpportunities = async() => {
        setState({ ...state, loading: true });
        const opps = await BetRouterApi.BetRouter.OpportunityApi.getMyOpenOpportunities();
        setState({
            ...state,
            router_orders: opps.router_orders,
            opportunities: opps.opportunities,
            opportunity_status_legs: opps.opportunity_status_legs,
            router_contests: opps.router_contests,
            loading: false
        })
    }

    // Filter and paginate opportunities
    const filteredOpportunities = opportunities.filter(opp => {
        if (!searchText) return true;

        // Search in opportunity type
        if (opp.opportunity_type.toLowerCase().includes(searchText.toLowerCase())) return true;

        // Search in order titles
        const oppOrders = router_orders.filter(order => order.opportunity_id === opp.opportunity_id);
        return oppOrders.some(order => order.title?.toLowerCase().includes(searchText.toLowerCase()));
    }).sort((a, b) => new Date(b.create_datetime).getTime() - new Date(a.create_datetime).getTime());

    const totalPages = Math.ceil(filteredOpportunities.length / ITEMS_PER_PAGE);
    const paginatedOpportunities = filteredOpportunities.slice(
        currentPage * ITEMS_PER_PAGE,
        (currentPage + 1) * ITEMS_PER_PAGE
    );

    const handleOpenDetail = (opp: OpportunityProps) => {
        setState({ ...state, selectedOpportunity: opp, showDetailModal: true });
    };

    const handleCloseDetail = () => {
        setState({ ...state, selectedOpportunity: null, showDetailModal: false });
    };


    const renderOpportunityCard = (data:{ item:OpportunityProps, index:number }) => {
        const opp = data.item;
        const isProfitable = (opp.expected_profit || 0) > 0;
        const oppOrders = router_orders.filter(order => order.opportunity_id === opp.opportunity_id);
        const firstOrder = oppOrders[0];
        const partner = partners.find(p => p.partner_id === firstOrder?.partner_id);
        const contest = router_contests.find(c => c.router_contest_id === opp.router_contest_id);

        return (
            <TouchableOpacity onPress={() => handleOpenDetail(opp)}>
                <View type='row' style={{ padding: 12, borderBottomWidth: 1, borderBottomColor: Colors.borders.light, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                    <View transparent style={{ flex: 1, flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                        {/* Contest/Type & Profit */}
                        <View transparent style={{ minWidth: 60 }}>
                            <Text theme='h2' size={11} numberOfLines={1}>
                                {contest?.contest_label || opp.opportunity_type.toUpperCase()}
                            </Text>
                            <Text
                                theme='h2'
                                size={13}
                                color={isProfitable ? Colors.text.success : Colors.text.warning}
                            >
                                ${(opp.expected_profit || 0).toFixed(2)}
                            </Text>
                        </View>

                        {/* Partner/Contest */}
                        <View transparent style={{ flex: 1, minWidth: 0 }}>
                            {firstOrder && (
                                <Text theme='description' size={11} numberOfLines={1}>
                                    {partner?.name || 'Unknown'}
                                </Text>
                            )}
                            <Text theme='description' size={10} numberOfLines={1}>
                                {oppOrders.length} leg{oppOrders.length !== 1 ? 's' : ''}
                            </Text>
                        </View>

                        {/* Bet */}
                        <View transparent style={{ minWidth: 40 }}>
                            <Text theme='h2' size={11}>
                                ${opp.total_bet_amount.toFixed(0)}
                            </Text>
                        </View>

                        {/* ROI */}
                        <View transparent style={{ minWidth: 45 }}>
                            <Text theme='h2' size={11}>
                                {opp.expected_roi.toFixed(1)}%
                            </Text>
                        </View>

                        {/* Status */}
                        <View transparent style={{ minWidth: 20 }}>
                            <Text theme='h2' size={13}>
                                {opp.fill_status === 'all_filled' ? '✓' :
                                 opp.fill_status === 'partially_filled' ? '⚠' :
                                 opp.fill_status === 'failed' ? '✗' : '⋯'}
                            </Text>
                        </View>
                    </View>

                    <Icons.ChevronIcon size={16} color={Colors.text.descriptionLight} direction="right" />
                </View>
            </TouchableOpacity>
        );
    };

    const renderOpportunities = (data:{ item:OpportunityProps, index:number }) => {
        const opp = data.item;
        const isProfitable = (opp.expected_profit || 0) > 0;

        // Get orders for this opportunity
        const oppOrders = router_orders.filter(order => order.opportunity_id === opp.opportunity_id);
        const contest = router_contests.find(c => c.router_contest_id === opp.router_contest_id);

        if (compact) {
            // Compact mode for desktop sidebar - show minimal info, clickable
            return (
                <TouchableOpacity onPress={() => handleOpenDetail(opp)}>
                    <View type='row' style={{ padding:10, borderBottomWidth:1, borderBottomColor: Colors.borders.light }}>
                        <View transparent style={{ flex:1 }}>
                            <View transparent style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom:6 }}>
                                <Text theme='h1' size={14} numberOfLines={1}>
                                    {contest?.contest_label || opp.opportunity_type.toUpperCase()}
                                </Text>
                                <Text
                                    theme='h2'
                                    size={14}
                                    color={isProfitable ? Colors.text.success : Colors.text.warning}
                                >
                                    ${(opp.expected_profit || 0).toFixed(2)}
                                </Text>
                            </View>

                            <View transparent style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Text theme='description' size={11}>
                                    {oppOrders.length} leg{oppOrders.length !== 1 ? 's' : ''} • ${opp.total_bet_amount.toFixed(2)}
                                </Text>
                                <Text theme='description' size={11}>
                                    ROI: {opp.expected_roi.toFixed(1)}%
                                </Text>
                            </View>
                        </View>
                    </View>
                </TouchableOpacity>
            );
        }

        // Mobile-friendly card layout
        return renderOpportunityCard(data);
    }

    const displayData = paginatedOpportunities;

    return (
        <View style={{ flex:1 }}>
            <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:compact ? 8 : 10, borderTopRightRadius:8, borderTopLeftRadius:8 }}>
              <View transparent style={{ flex:1 }}>
                <Text theme='h1' style={{ fontSize: compact ? 14 : 16 }}>
                    {compact ? 'My Bets' : 'My Active Opportunities'}
                </Text>
                {compact && (
                    <Text theme='description' style={{ fontSize:11, marginTop:2 }}>
                        {opportunities.length} active
                    </Text>
                )}
              </View>
            </View>

            {/* Search */}
            <View transparent style={{ padding: compact ? 8 : 12, paddingBottom: 0 }}>
                <SearchBox
                    placeholder="Search by contest..."
                    onChange={(text) => setState({ ...state, searchText: text, currentPage: 0 })}
                    hide_search_button
                />
            </View>

            <View type='body' style={{ flex:1 }}>
                {loading ? (
                    <View transparent style={{ padding:10 }}>
                        <Text theme='description' style={{ fontSize: compact ? 11 : 13 }}>
                            Loading...
                        </Text>
                    </View>
                ) : filteredOpportunities.length === 0 ? (
                    <View transparent style={{ padding:10 }}>
                        <Text theme='description' style={{ fontSize: compact ? 11 : 13 }}>
                            {searchText ? 'No opportunities match your search' : 'No active opportunities'}
                        </Text>
                    </View>
                ) : (
                    <>
                        <FlatList
                            key='my_opps_list'
                            data={displayData}
                            keyExtractor={(item) => item.opportunity_id}
                            renderItem={renderOpportunities}
                        />

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <View transparent style={{ padding: compact ? 8 : 12 }}>
                                <Pagination
                                    offset={currentPage}
                                    pages={totalPages}
                                    onSelectPage={(page) => setState({ ...state, currentPage: page })}
                                />
                                <Text theme='description' size={compact ? 10 : 11} style={{ marginTop: 8, textAlign: 'center' }}>
                                    Showing {currentPage * ITEMS_PER_PAGE + 1}-{Math.min((currentPage + 1) * ITEMS_PER_PAGE, filteredOpportunities.length)} of {filteredOpportunities.length}
                                </Text>
                            </View>
                        )}
                    </>
                )}
            </View>

            <PlacedOpportunityModal
                visible={showDetailModal}
                opportunity={selectedOpportunity}
                orders={router_orders}
                partners={partners}
                onClose={handleCloseDetail}
                insets={insets}
            />
        </View>
    )
}

export default MyOpportunities
